import { countClass, pointer, queryAllClass, queryClass } from './parse';
import type { ParseRecord } from './parse';
import { loadWatchedLectures } from './studentUtilities';
import { readLocalProgressEntries, type VideoProgressEntry } from './videoProgress';

/* ---------- Miscellaneous content library ---------- */

export type MiscCategory = ParseRecord & { CatName?: string; CatDesc?: string };
export type MiscItem = ParseRecord & { Name?: string; Title?: string; Description?: string; Link?: string; FileType?: string; MaterialPaidType?: string; PaidType?: string };

export function loadMiscCategories(): Promise<MiscCategory[]> {
  return queryAllClass('elearning_MiscellCategories', {
    order: 'createdAt',
    where: (query) => query.notEqualTo('IsDeleted', true),
  }) as Promise<MiscCategory[]>;
}

export async function loadMiscCategoryCounts(categories: MiscCategory[]): Promise<Map<string, number>> {
  const counts = await Promise.all(categories.map((category) =>
    countClass('elearning_CourseMaterial', {
      where: (query) => query.equalTo('CatName', pointer('elearning_MiscellCategories', category.objectId)),
    }).catch(() => 0),
  ));
  return new Map(categories.map((category, index) => [category.objectId, counts[index]]));
}

/** Items inside a category (top level) or one of its folders. */
export function loadMiscItems(categoryId: string, parentFolderId?: string): Promise<MiscItem[]> {
  return queryAllClass('elearning_CourseMaterial', {
    order: '-createdAt',
    where: (query) => {
      query.equalTo('CatName', pointer('elearning_MiscellCategories', categoryId));
      if (parentFolderId) query.equalTo('ParentFolder', pointer('elearning_CourseMaterial', parentFolderId));
      else query.equalTo('ParentFolder', null);
    },
  }) as Promise<MiscItem[]>;
}

export async function loadMiscItem(itemId: string): Promise<MiscItem | undefined> {
  const rows = await queryClass('elearning_CourseMaterial', {
    limit: 1,
    where: (query) => query.equalTo('objectId', itemId),
  });
  return rows[0] as MiscItem | undefined;
}

export type FileKind = 'folder' | 'pdf' | 'image' | 'other';

export function miscFileKind(item: Pick<MiscItem, 'FileType'>): FileKind {
  const type = String(item.FileType ?? '').trim().toLowerCase();
  if (type === 'folder') return 'folder';
  if (type === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg'].includes(type)) return 'image';
  return 'other';
}

/* ---------- Free videos & free tests ---------- */

export type FreeVideo = ParseRecord & { VideoName?: string; VideoLogo?: string | Record<string, unknown>; VideoDuration?: number; VideoPaidType?: string };

export async function loadFreeVideosPage(page = 0, pageSize = 15): Promise<{ items: FreeVideo[]; total: number }> {
  // Legacy seeFreeVideos.js: Free originals at the top level of the video tree.
  const where = (query: QueryFilter) => {
    query.equalTo('VideoPaidType', 'Free');
    query.equalTo('Type', 'Original');
    query.equalTo('ParentFolder', null);
  };
  const [items, total] = await Promise.all([
    queryClass('elearning_Videos', { limit: pageSize, skip: page * pageSize, order: 'createdAt', where }) as Promise<FreeVideo[]>,
    countClass('elearning_Videos', { where }),
  ]);
  return { items, total };
}

export async function loadFreeVideos(limit = 10): Promise<FreeVideo[]> {
  const { items } = await loadFreeVideosPage(0, limit);
  return items.filter((video) => video.VideoName);
}

export type FreeTest = ParseRecord & { TestTitle?: string; CourseTitle?: string; NoOfQuestions?: number | string; Time?: number | string; TotalMarks?: number | string; TestPaidType?: string };

type QueryFilter = { equalTo: (k: string, v: unknown) => unknown; notEqualTo: (k: string, v: unknown) => unknown; exists: (k: string, v?: boolean) => unknown };

function freeTestWhere(query: QueryFilter) {
  query.equalTo('IsPublished', true);
  query.notEqualTo('Type', 'Folder');
  query.equalTo('TestPaidType', 'Free');
  query.exists('CoursePtr');
  query.equalTo('ParentFolder', null);
}

export async function loadFreeTestsPage(page = 0, pageSize = 15): Promise<{ items: FreeTest[]; total: number }> {
  const [items, total] = await Promise.all([
    queryClass('test_TestInfo', { limit: pageSize, skip: page * pageSize, order: 'createdAt', where: freeTestWhere }) as Promise<FreeTest[]>,
    countClass('test_TestInfo', { where: freeTestWhere }),
  ]);
  return { items, total };
}

export async function loadFreeTests(limit = 5): Promise<FreeTest[]> {
  const { items } = await loadFreeTestsPage(0, limit);
  return items;
}

/* ---------- Recent videos (watched lectures, most recent first) ---------- */

export type RecentVideo = VideoProgressEntry & { video?: ParseRecord };

/**
 * The player re-appends an entry on every save, so the tail of the Bookmarks
 * array is the watch history. Server order wins; local-only entries fill gaps
 * for preview logins and offline saves.
 */
export async function loadRecentVideos(limit = 10): Promise<RecentVideo[]> {
  let serverEntries: VideoProgressEntry[] = [];
  try {
    serverEntries = (await loadWatchedLectures()).Bookmarks ?? [];
  } catch { /* offline or preview login */ }
  const localEntries = readLocalProgressEntries();
  const merged = new Map<string, VideoProgressEntry>();
  for (const entry of [...localEntries, ...serverEntries]) {
    if (typeof entry?.id === 'string' && entry.id.trim()) merged.set(entry.id, entry);
  }
  const history = [...merged.values()].reverse().slice(0, limit);
  if (!history.length) return [];
  const ids = history.map((entry) => entry.id);
  const rows = await queryClass('elearning_Videos', {
    limit: ids.length,
    keys: ['VideoName', 'VideoLogo', 'VideoDuration'],
    appScoped: false,
    where: (query) => query.containedIn('objectId', ids),
  }).catch(() => [] as ParseRecord[]);
  const titles = new Map(rows.map((row) => [row.objectId, row]));
  return history.flatMap((entry) => {
    const video = titles.get(entry.id);
    return video ? [{ ...entry, video }] : [];
  });
}
